import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate, transformDateToLocalDateString } from "helpers";

// UI
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { CheckBox } from "ui/components/CheckBox";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Badge } from "ui/components/Badge";

import { InvoiceEditCardProps } from "./types";
import { useHandleTextFieldChange, useHandleCheckboxChange, useHandleDropdownChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { INVOICE_EDIT_FORM_SCHEMA } from "./validation";
import { useDispatch } from "react-redux";
import { updateSettings } from "services/api/settings/redux";
import { FiscalizationCertificateInfo } from "services/api/settings/types";
import {
  getFiscalizationCertificateAPI,
  deleteFiscalizationCertificateAPI,
} from "services/api/settings/fiscalization/api";
import CertificateUploadModal from "../CertificateUploadModal/CertificateUploadModal";

const FISCALIZATION_MODE_OPTIONS = [
  { value: true, text: "Test (Demo CIS)" },
  { value: false, text: "Produkcija" },
];

const InvoiceEditCard: React.FC<InvoiceEditCardProps> = (props) => {
  const { show, setting, setSetting, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const [certInfo, setCertInfo] = useState<FiscalizationCertificateInfo | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    if (!show) return;
    const fetchCert = async () => {
      try {
        const result = await getFiscalizationCertificateAPI();
        setCertInfo(result);
      } catch {
        setCertInfo(null);
      }
    };
    fetchCert();
  }, [show]);

  const handleDeleteCertificate = useCallback(async () => {
    try {
      await deleteFiscalizationCertificateAPI();
      setCertInfo(null);
    } catch {}
  }, []);

  const handleCertificateUploadSuccess = useCallback((info: FiscalizationCertificateInfo) => {
    setCertInfo(info);
  }, []);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(setting, INVOICE_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) return;

    setIsLoading(true);

    try {
      if (setting.id) {
        const result = await dispatch(updateSettings(setting) as any);
        if (result?.ok && onSuccess) onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setSetting,
    setting
  );

  const handleCheckboxChange = useHandleCheckboxChange(setSetting);

  const handleDropdownChange = useHandleDropdownChange(
    setSetting as any,
    setting
  );

  return (
    <Modal show={show} onHide={onClose}>
      <ModalHeader
        title="Uredi postavke računa"
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />
      <ModalBody>
        {show && (
          <div className="flex gap-4">
            <div className="w-full m-auto">
              <div className="flex gap-3 h-full">
                <form noValidate className="text-left flex-1" onSubmit={handleFormSubmit}>
                  <div className="p-4">
                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="invoiceFormatNumber"
                        label="Format broja računa"
                        placeholder="Format broja računa"
                        onChange={handleTextFieldChange}
                        value={setting.invoiceFormatNumber}
                        error={errors.invoiceFormatNumber}
                        forceShowError
                      />
                    </div>

                    <div className="mb-3">
                      <ul className="common-list text-sm">
                        <li>{t("admin.setting.document.invoice.number.token.yyyy")}</li>
                        <li>{t("admin.setting.document.invoice.number.token.yy")}</li>
                        <li>{t("admin.setting.document.invoice.number.token.mm")}</li>
                        <li>{t("admin.setting.document.invoice.number.token.dd")}</li>
                        <li>{t("admin.setting.document.invoice.number.token.id")}</li>
                      </ul>
                    </div>

                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="invoicePaymentIBAN"
                        label="IBAN za uplatu"
                        placeholder="IBAN za uplatu"
                        onChange={handleTextFieldChange}
                        value={setting.invoicePaymentIBAN}
                        error={errors.invoicePaymentIBAN}
                      />
                    </div>

                    <p className="text-base font-bold text-slate-600 py-3 mt-3">
                      Fiskalizacija
                    </p>

                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="tenantOib"
                        label="OIB tvrtke"
                        placeholder="OIB tvrtke"
                        onChange={handleTextFieldChange}
                        value={setting.tenantOib}
                        error={errors.tenantOib}
                      />
                    </div>

                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="operaterOib"
                        label="OIB operatera"
                        placeholder="OIB operatera"
                        onChange={handleTextFieldChange}
                        value={setting.operaterOib}
                        error={errors.operaterOib}
                      />
                    </div>

                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="businessUnitMark"
                        label="Oznaka poslovnog prostora"
                        placeholder="PP1"
                        onChange={handleTextFieldChange}
                        value={setting.businessUnitMark}
                        error={errors.businessUnitMark}
                      />
                    </div>

                    <div className="mb-3">
                      <TextField
                        type="text"
                        name="cashRegisterMark"
                        label="Oznaka naplatnog uređaja"
                        placeholder="1"
                        onChange={handleTextFieldChange}
                        value={setting.cashRegisterMark}
                        error={errors.cashRegisterMark}
                      />
                    </div>

                    <div className="mb-3">
                      <Dropdown
                        name="fiscalizationDemoMode"
                        label="Način fiskalizacije"
                        options={FISCALIZATION_MODE_OPTIONS}
                        value={setting.fiscalizationDemoMode}
                        onChange={handleDropdownChange}
                      />
                    </div>

                    <div className="mb-3">
                      <CheckBox
                        name="inVatSystem"
                        label="U sustavu PDV-a"
                        checked={setting.inVatSystem}
                        onChange={handleCheckboxChange}
                      />
                    </div>

                    <p className="text-base font-bold text-slate-600 py-3 mt-3">
                      Certifikat
                    </p>

                    {certInfo && certInfo.hasCertificate ? (
                      <div className="border border-slate-200 rounded-sm p-3 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge text="Aktivan" variant="success" height="small" showDot />
                        </div>
                        <div className="text-sm mb-1">
                          <span className="text-slate-500">Subjekt: </span>
                          <span className="font-medium">{certInfo.subject || "-"}</span>
                        </div>
                        <div className="text-sm mb-1">
                          <span className="text-slate-500">Vrijedi od: </span>
                          <span>{transformDateToLocalDateString(certInfo.validFrom) || "-"}</span>
                        </div>
                        <div className="text-sm mb-2">
                          <span className="text-slate-500">Vrijedi do: </span>
                          <span>{transformDateToLocalDateString(certInfo.validTo) || "-"}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outlined" size="small" onClick={() => setShowUploadModal(true)}>
                            Zamijeni
                          </Button>
                          <Button variant="outlined" size="small" onClick={handleDeleteCertificate}>
                            Ukloni
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <Button variant="outlined" size="small" onClick={() => setShowUploadModal(true)}>
                          Učitaj certifikat
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                    <Button
                      variant="outlined"
                      children={t("admin.close")}
                      onClick={() => onClose()}
                    />
                    <Button
                      variant="primary"
                      children={t("admin.save")}
                      type="submit"
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <CertificateUploadModal
      
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onSuccess={handleCertificateUploadSuccess}
      />
    </Modal>
  );
};
export default memo(InvoiceEditCard);
